"use client";

import { useInspectorMode } from "@utils/ContentfulUtils";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import React, { useContext, useEffect, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { UserContext } from "@/context/UserContext";
import { ctaClickTrackingWithProjectName, pushMetaversCtaClick } from "@/dataLayer/dataLayer";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import useMetaverseRouteChangeHandler from "@/hooks/useMetaverse";
import { BaseEntry, CustomLink, Media } from "@/types";
import cn from "@/utils/cn";
import { getLink } from "@/utils/linkUtils";

import Icon from "../ui/Icon/Icon";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";

interface HeroProjectCommunityCard extends BaseEntry {
  video: Media;
  video_mobile?: Media;
  videoCoverImage: Media;
  logo?: Media;
  videoTitle?: string;
  ctaLink1?: CustomLink;
  ctaLink2?: CustomLink;
  vodDesktop?: string;
  vodMobile?: string;
}

interface Props {
  entry: HeroProjectCommunityCard;
  h1ID?: string;
  animation?: boolean;
  singleSlide?: boolean;
  priority?: boolean;
}

export default function HeroProjectCommunityCard({ entry, h1ID, animation, singleSlide, priority }: Props) {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isSo = useIsSaleScreen();
  const inspectorMode = useInspectorMode(entry.id);
  const t = useTranslations();
  const locale = useLocale();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVideoUrl, setModalVideoUrl] = useState("");
  const [modalTitle, setModalTitle] = useState("Video");
  const { setIsVideoModalOpenIframe } = useContext(UserContext);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const underlineButtonClasses = cn("btn btn-tertiary-white btn-medium font-medium border-0 md:p-0");
  const heroClasses = cn("btn btn-primary-white block !border-white hover:!border-0 text-center btn-medium font-medium border-0");
  useMetaverseRouteChangeHandler(isIframeLoaded);

  useEffect(() => {
    const mainContainer = document.querySelector("main");
    if (mainContainer && animation) {
      mainContainer.style.display = "flex";
      mainContainer.style.flexDirection = "column";
    }
  }, [animation]);

  const rawCoverUrl = entry.videoCoverImage?.url;
  const posterSrc = rawCoverUrl ? `${rawCoverUrl}?fm=webp&w=828&q=80&fit=fill` : undefined;

  const videoRef = useRef<HTMLVideoElement>(null);

  const videoSrc = isMobile ? entry?.video_mobile?.url || entry?.video?.url : entry?.video?.url;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.defaultMuted = true;
      videoRef.current.playsInline = true;

      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn("Autoplay failed:", error);
        });
      }
    }
  }, [videoSrc]);

  if (isSo) return null;

  return (
    <div className="relative flex justify-center h-svh">
      <div className="absolute z-10 w-full h-full img-overlay-homepage-hero"></div>
      {priority && posterSrc && <link rel="preload" as="image" href={posterSrc} fetchPriority="high" />}
      {posterSrc && (
        <img
          src={posterSrc}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          className="absolute top-0 left-0 w-full h-full object-cover"
          alt=""
        />
      )}
      {videoSrc && (
        <video
          ref={videoRef}
          src={videoSrc}
          poster={posterSrc}
          autoPlay
          loop
          muted
          playsInline
          preload={priority ? "auto" : "none"}
          {...({ fetchPriority: priority ? "high" : "auto" } as React.VideoHTMLAttributes<HTMLVideoElement>)}
          className="videoHero absolute top-0 left-0 w-full h-full object-cover"
          {...inspectorMode.getProps("video")}
        />
      )}
      <div className={cn("absolute bottom-0 z-20 left-1/2 -translate-x-1/2 mb-[40px] w-full", animation && singleSlide && "-translate-y-1/2")}>
        <div className="flex flex-col items-center">
          {entry.logo && (
            <Image
              src={entry.logo?.url}
              alt={entry.logo?.title}
              width={Math.min(entry.logo?.width || 310, 310)}
              height={Math.min(entry.logo?.height || 157, 157)}
              priority
              {...inspectorMode.getProps("logo")}
            />
          )}
          {entry?.videoTitle && (
            <span
              className="font-medium text-white uppercase  font-primary text-l md:text-xl md:whitespace-wrap text-center px-xxs md:px-0"
              {...inspectorMode.getProps("videoTitle")}
            >
              {h1ID === entry.id ? <h1>{entry.videoTitle}</h1> : entry.videoTitle}
              {/* {entry.videoTitle} */}
            </span>
          )}
          <div className={cn(entry?.ctaLink1 && entry?.ctaLink2 && "flex flex-col gap-m md:flex-row md:gap-l items-center", "mt-l ")}>
            {entry.ctaLink1 &&
              (entry?.ctaLink1?.isVideo ? (
                <Button
                  rounded
                  size={ButtonSize.MEDIUM}
                  theme={ButtonTheme.WHITE}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  // customClass="text-white underline underline-offset-2"
                  onClick={() => {
                    if (entry?.ctaLink1?.isVideo) {
                      pushMetaversCtaClick(entry?.ctaLink1?.label ?? "");
                    }
                    setModalOpen(true);
                    setModalVideoUrl(entry?.ctaLink1?.link?.url || "");
                    setModalTitle(entry?.ctaLink1?.modalLabel || "Video");
                  }}
                >
                  {entry.ctaLink1.label}
                </Button>
              ) : (
                <Button
                  href={getLink(entry.ctaLink1, locale)}
                  className={heroClasses}
                  type="button"
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.WHITE}
                  {...inspectorMode.getProps("ctaLink1")}
                  onClick={(e) => {
                    ctaClickTrackingWithProjectName(
                      entry?.ctaLink1?.label ?? "",
                      "HeroProjectCommunityCard",
                      "HeroProjectCommunityCard",
                      entry?.ctaLink1?.link?.content?.slug ?? "",
                    );
                  }}
                >
                  {entry.ctaLink1.label}
                </Button>
              ))}
            {entry.ctaLink2 &&
              (!entry?.ctaLink2?.isVideo ? (
                <Button
                  type="button"
                  className={underlineButtonClasses}
                  href={getLink(entry.ctaLink2, locale)}
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.WHITE}
                  {...inspectorMode.getProps("ctaLink2")}
                >
                  {entry.ctaLink2.label}
                </Button>
              ) : (
                <Button
                  rounded
                  size={ButtonSize.MEDIUM}
                  // theme={ButtonTheme.WHITE}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  customClass="text-white underline underline-offset-2 hover:pointer"
                  onClick={() => {
                    if (entry?.ctaLink2?.isVideo) {
                      pushMetaversCtaClick(entry?.ctaLink2?.label ?? "");
                    }
                    setModalOpen(true);
                    setModalVideoUrl(entry?.ctaLink2?.link?.url || "");
                    setModalTitle(entry?.ctaLink2?.modalLabel || "Video");
                  }}
                >
                  {entry.ctaLink2.label}
                </Button>
              ))}
          </div>
        </div>
      </div>
      {
        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
        >
          <ModalContent className="h-dvh w-dvw !rounded-none pt-0 md:pt-m !px-0 md:!px-l">
            <div className={cn("flex items-center justify-between mb-l px-m md:px-0 pt-l")}>
              <p className={cn("font-primary uppercase")}>{modalTitle}</p>
              <Button
                className={"stroke-black"}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                theme={ButtonTheme.BLACK}
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                <span className="pointer-events-none">
                  <Icon name="close" size="24" title={t("label.common.close")} />
                  <span className="sr-only">{t("button.label.closeModal")}</span>
                </span>
              </Button>
            </div>
            {
              <iframe
                className={cn("object-cover w-full h-[calc(100dvh-72px)] md:rounded-lg border-0")}
                width="560"
                height="315"
                src={modalVideoUrl}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => {
                  setIsIframeLoaded(true);
                }}
              ></iframe>
            }
          </ModalContent>
        </Modal>
      }
    </div>
  );
}
